import React, { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useParams, Link } from 'react-router-dom';

import i18n from 'I18n';

import AuthButton from 'components/base/AuthButton';
import ContentSections from 'components/base/ContentSections';
import CustomBreadcrumb from 'components/base/CustomBreadcrumb';
import CustomListComponent from 'components/base/CustomListComponent';
import ListButtonsGroup from 'components/base/ListButtonsGroup';
import Loading from 'components/base/Loading';
import BasePrompt from 'components/base/prompt/BasePrompt';
import MembershipInfoCard from 'components/customer/MembershipInfoCard';
import {
  APIStatus,
  EarningRuleRewardType,
  EarningRuleType,
  ImageDomain,
  LanguageConfig,
  WEEKDAY,
} from 'config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';
import { ToastType } from 'models/NavBarModel';

import { createAction } from 'utils';
import { getCampaignPeriodDateTime, getReadablePeriod } from 'utils/TimeFormatUtil';
import { LinkSection } from 'components/base/LinkSection';
import { DISPLAY_REWARD_LOGIC_TYPE, REWARD_LOGIC_TYPE } from 'components/earning/RewardLogicSelector';
import { DISPLAY_REWARD_AMOUNT_BASED_TYPE, REWARD_AMOUNT_TYPE } from 'components/earning/RewardAmountInputArea';
import { EARNING_RULE_PERIODIC_TYPE } from 'components/earning/PurchaseEarningRuleRewardLimitInputs';
import CustomButtonWithTips from 'components/base/CustomButtonWithTips';
import { ImportResource } from 'models/DownloadImportModel';

function EarningRuleDetail({
  earningRule,
  languages,
  createStatus,
  levelSource,
  webhookAuthenticatedKey,
  isCopied,
  skusHaveLoaded,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const params = useParams();
  const { id } = params;

  const [showPrompt, setShowPrompt] = useState(false);
  const [promptConfig, setPromptConfig] = useState({});

  useEffect(() => {
    if (earningRule?.type === EarningRuleType.levelUpgrade && !levelSource) {
      dispatch(createAction('levels/getLevelList')({}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earningRule])

  const getListStrings = (list, linkPrefix = "", linkText = "View detail") => {
    if (list?.length === 0) {
      return null;
    }
    return (
      <ul>
        {list?.map((item) => (
          <li>
            {`[ID: ${item.pk}] ${item.name}`}
            {linkPrefix && <Link className="profile-section-link" style={{ fontSize: "14px" }} to={`${linkPrefix}/${item.pk}/`}>{linkText}</Link>}
          </li>
        ))}
      </ul>
    );
  };

  const getRewardFields = () => {
    if (!earningRule.rewardType) {
      return {
        displayRewardType: "",
        rewardFields: [],
      }
    }
    const isGeneralPurchaseEarningRule = earningRule?.type === EarningRuleType.generalPurchase
    if (earningRule.rewardType === EarningRuleRewardType.points) {
      if (isGeneralPurchaseEarningRule) {
        return {
          displayRewardType: "Points",
          rewardFields: [
            [{ title: 'Reward logic', field: 'displayRewardLogicType' }],
            [{ title: 'Reward type', field: 'rewardType' }],
            [{ title: 'Reward amount', field: 'displayRewardAmount' }],
          ],
        }
      }
      return {
        displayRewardType: "Points",
        rewardFields: [
          [{ title: 'Link to campaign', field: 'lindedCampaigns', customRender: getListStrings(earningRule?.lindedCampaigns, "/campaigns", "View campaign") }],
          [{ title: 'Reward type', field: 'rewardType' }],
          [{ title: 'Quantity', field: 'quantity' }],
          [{ title: 'Progression Points', field: 'tpeQuantity' }],
        ],
      }
    }
    if (earningRule.rewardType === EarningRuleRewardType.coupons) {
      if (isGeneralPurchaseEarningRule) {
        return {
          displayRewardType: "Coupons",
          rewardFields: [
            [{ title: 'Reward logic', field: 'displayRewardLogicType' }],
            [{ title: 'Reward type', field: 'rewardType' }],
            [{
              title: 'Link to coupon set', field: '', customRender: (
                <div className='d-flex'>
                  <label className="custom-record-normal-font custom-timeline-time-color list-new-line">
                    {`[ID: ${earningRule?.coupons?.pk}] ${earningRule?.coupons?.name}`}
                  </label>
                  <LinkSection
                    link={`/coupons/${earningRule?.coupons?.pk}`}
                    linkText={'View coupon set'}
                    disableLink={earningRule?.coupons?.toDelete}
                    disableLinkText="Deleted"
                  />
                </div>
              )
            }],
            [{ title: 'Reward amount', field: 'displayRewardAmount' }],
          ],
        }
      }
      return {
        displayRewardType: "Coupons",
        rewardFields: [
          [{ title: 'Link to campaign', field: 'lindedCampaigns', customRender: getListStrings(earningRule?.lindedCampaigns, "/campaigns", "View campaign") }],
          [{ title: 'Reward type', field: 'rewardType' }],
          [{
            title: 'Link to coupon set', field: '', customRender: (
              <div className='d-flex'>
                <label className="custom-record-normal-font custom-timeline-time-color list-new-line">
                  {`[ID: ${earningRule?.coupons?.pk}] ${earningRule?.coupons?.name}`}
                </label>
                <LinkSection
                  link={`/coupons/${earningRule?.coupons?.pk}`}
                  linkText={'View coupon set'}
                  disableLink={earningRule?.coupons?.toDelete}
                  disableLinkText="Deleted"
                />
              </div>
            )
          }],
          [{ title: 'Quantity', field: 'quantity' }],
        ]
      }
    }
    if (earningRule.rewardType === EarningRuleRewardType.stamps) {
      if (isGeneralPurchaseEarningRule) {
        return {
          displayRewardType: "Stamps",
          rewardFields: [
            [{ title: 'Link to stamp campaign', field: 'lindedStampCampaigns', customRender: getListStrings(earningRule?.lindedStampCampaigns, "/stamp_campaigns", "View campaign") }],
            [{ title: 'Reward logic', field: 'displayRewardLogicType' }],
            [{ title: 'Reward type', field: 'rewardType' }],
            [{ title: 'Reward amount', field: 'displayRewardAmount' }],
          ],
        }
      }
      return {
        displayRewardType: "Stamps",
        rewardFields: [
          [{ title: 'Link to stamp campaign', field: 'lindedStampCampaigns', customRender: getListStrings(earningRule?.lindedStampCampaigns, "/stamp_campaigns", "View campaign") }],
          [{ title: 'Reward type', field: 'rewardType' }],
          [{ title: 'Reward stamps', field: 'displayStampReward' }],
        ]
      }
    }
    return {
      displayRewardType: "",
      rewardFields: [],
    }
  }
  const {
    displayRewardType,
    rewardFields,
  } = useMemo(getRewardFields, [earningRule.rewardType])


  const limitValueFromEarningRule = {
    overallLimit: earningRule?.overallLimit,
    periodicLimit:
      earningRule?.periodicLimit && earningRule?.periodicLimitDays
        ? `${earningRule?.periodicLimit} times per ${earningRule?.periodicLimitDays} days`
        : '',
    perHeadLimit: earningRule?.perHeadLimit,
    perHeadPeriodicLimit:
      earningRule?.perHeadPeriodicLimit && earningRule?.perHeadPeriodicLimitDays
        ? `${earningRule?.perHeadPeriodicLimit} times per ${earningRule?.perHeadPeriodicLimitDays} days`
        : '',
  };

  const limitFields = [
    [{ title: 'Overall Limit (optional)', field: 'overallLimit' }],
    [{ title: 'Periodic Limit (optional)', field: 'periodicLimit' }],
    [{ title: 'Per head Limit (optional)', field: 'perHeadLimit' }],
    [
      {
        title: 'Per head periodic Limit (optional)',
        field: 'perHeadPeriodicLimit',
      },
    ],
  ];
  const levelUpgradelimitFields = [
    [
      {
        title: 'Per head periodic Limit (optional)',
        field: 'perHeadPeriodicLimit',
      },
    ],
  ];

  const renderLevelGoal = (levelGoal) => {
    if (levelSource && earningRule.type === EarningRuleType.levelUpgrade) {
      let fromLevel = levelSource.find(
        (level) => level.pk === levelGoal.pk - 1,
      );
      return fromLevel?.levelName + ' to ' + levelGoal.levelName;
    } else {
      return '-';
    }
  };

  const targetCustomerDisplayFields = [
    [{ title: 'Exclusive', field: 'displayIsExclusive' }],
    [{ title: 'Target customer', field: 'displayTargetCustomerType' }],
    [{ title: 'Static segment', field: 'displayStaticSegments' }],
    [{ title: 'Dynamic segment', field: 'displayDynamicSegments' }],
    [{ title: 'Target levels', field: 'displayTargetedLevels' }],
    [{ title: 'Exclude blacklist segment', field: 'displayExcludeBlacklistSegment' }],
    [{ title: 'Blacklist segment', field: 'displayBlacklistSegments' }],
  ]
  const dataSource = {
    [EarningRuleType.generalPurchase]: [{
      title: 'General Purchase',
      data: {
        ...earningRule,
        selectedStores: earningRule?.selectedStores?.map((item) => item.displayStoreName),
        specialSkus: earningRule?.specialSkus
          ?.map((item) => item.name)
          .concat(earningRule?.inputSkus?.map((item) => item)),
        displayIsExcludeDeliveryCost: earningRule?.isExcludeDeliveryCost
          ? 'Yes'
          : 'No',
        displayIsExcludeOtherCharges: earningRule?.isExcludeOtherCharges
          ? 'Yes'
          : 'No',
        displayBlackoutPeriod: earningRule?.activePeriodValues?.map((item) =>
          getReadablePeriod(item.startDate, item.endDate, false),
        ),
        displayBlackoutWeekend: earningRule?.activeWeekday?.map(
          (item) => WEEKDAY[item],
        ),
        displayLineItemRequirement: earningRule?.lineItemRequirement ? "Yes" : "No",
        ...limitValueFromEarningRule,
      },
      fields: [
        [{ title: 'For stamp campaign', field: 'displayLinkedStampCampaign' }],
        [{ title: 'Store (optional)', field: 'selectedStores' }],
        [{ title: 'Line item requirement (optional)', field: 'displayLineItemRequirement' }],
        [{ title: 'Transaction', field: 'lineItemGroups' }],
        [{ title: 'Minimum spending per transaction', field: 'minSpending' }],
        [{ title: 'Minimum quantity per transaction', field: 'minQuantity' }],
        [{ title: 'Import SKU (optional)', field: 'specialSkus' }],
        [
          {
            title: 'Exclude delivery cost (for online purchase)',
            field: 'displayIsExcludeDeliveryCost',
          },
        ],
        [
          {
            title: 'Exclude other charges (for online purchase)',
            field: 'displayIsExcludeOtherCharges',
          },
        ],
        [
          {
            title: 'Accept receipt X days after purchase for system handling (optional)',
            field: 'eligibleDays',
          },
        ],
        [
          {
            title: 'Upload receipt X days after purchase (optional)',
            field: 'uploadEligibleDays',
          },
        ],
        [{ title: 'In birth-month', field: 'displayIsInBirthMonth' }],
        [{ title: 'Recurring active period', field: 'displayRecurringActivePeriod' }],
        ...targetCustomerDisplayFields,
        [{ title: 'Active period', field: 'displayActivePeriod' }],
        [
          {
            title: 'Blackout period (by period)',
            field: 'displayBlackoutPeriod',
          },
        ],
        [
          {
            title: 'Blackout period (by weekday)',
            field: 'displayBlackoutWeekend',
          },
        ],
      ],
    }],
    [EarningRuleType.gpsCheckIn]: [{
      title: 'GPS Check-in',
      data: { ...earningRule, ...limitValueFromEarningRule },
      fields: [
        [{ title: 'For stamp campaign', field: 'displayLinkedStampCampaign' }],
        [{ title: 'Latitude', field: 'latitude' }],
        [{ title: 'Longitude', field: 'longitude' }],
        [{ title: 'Radius', field: 'radius' }],
        ...limitFields,
      ],
    }],
    [EarningRuleType.memberReferral]: [{
      title: 'Referral',
      data: earningRule,
      fields: [
        [{ title: 'For stamp campaign', field: 'displayLinkedStampCampaign' }],
        [{ title: 'Beneficiary', field: 'referralType' }],
        [
          {
            title: 'Maximum number of invitees (optional, for inviters only)',
            field: 'maxInvitees',
          },
        ],
      ],
    }],
    [EarningRuleType.birthday]: [{
      title: 'Birthday',
      data: {
        ...earningRule,
        displayBlackoutPeriod: earningRule?.activePeriodValues?.map((item) =>
          getReadablePeriod(item.startDate, item.endDate, false),
        ),
        displayBlackoutWeekend: earningRule?.activeWeekday?.map(
          (item) => WEEKDAY[item],
        ),
        // birthdayPeriod: `${earningRule?.birthStart} - ${earningRule?.birthEnd}`,
      },
      fields: [
        [{ title: 'For stamp campaign', field: 'displayLinkedStampCampaign' }],
        // [{ title: 'Birthday Period', field: 'birthdayPeriod' }],
        ...targetCustomerDisplayFields,
        [{ title: 'Recurring active period', field: 'displayRecurringActivePeriod' }],
        [{ title: 'Active period', field: 'displayActivePeriod' }],
        [
          {
            title: 'Blackout period (by period)',
            field: 'displayBlackoutPeriod',
          },
        ],
        [
          {
            title: 'Blackout period (by weekday)',
            field: 'displayBlackoutWeekend',
          },
        ],
      ],
    }],
    [EarningRuleType.qrCodeScanning]: [{
      title: 'QR Code Scanning',
      data: { ...earningRule, ...limitValueFromEarningRule },
      fields: [
        [{ title: 'For stamp campaign', field: 'displayLinkedStampCampaign' }],
        [{ title: 'QR codes', field: 'qrCodes' }],
        ...limitFields,
      ],
    }],
    [EarningRuleType.newMember]: [
      {
        title: 'New Member',
        data: {
          ...earningRule,
          displayBlackoutPeriod: earningRule?.activePeriodValues?.map((item) =>
            getReadablePeriod(item.startDate, item.endDate, false),
          ),
          displayBlackoutWeekend: earningRule?.activeWeekday?.map(
            (item) => WEEKDAY[item],
          ),
        },
        fields: [
          [{ title: 'For stamp campaign', field: 'displayLinkedStampCampaign' }],
          ...targetCustomerDisplayFields,
          [{ title: 'Active period', field: 'displayActivePeriod' }],
          [
            {
              title: 'Blackout period (by period)',
              field: 'displayBlackoutPeriod',
            },
          ],
          [
            {
              title: 'Blackout period (by weekday)',
              field: 'displayBlackoutWeekend',
            },
          ],
        ],
      },
      {
        title: "promotion code",
        data: {
          ...earningRule,
          displayRequirePromotionCode: earningRule?.requirePromotionCode ? "Yes" : "No",
          displayPromotionCodeUsageInfo:
            `Total times: ${earningRule?.promotionCodeUsageMaxLimit || '-'}\n` +
            `Available times: ${earningRule?.promotionCodeAvailableTimes || '-'}`
        },
        fields: [
          [{ title: 'Require promotion code', field: 'displayRequirePromotionCode' }],
          [{ title: 'Promotion code', field: 'promotionCode' }],
          [{ title: 'Maximum number of time this code can be used', field: 'displayPromotionCodeUsageInfo' }],
        ],
      }],
    [EarningRuleType.fillingForm]: [{
      title: 'Webhook',
      data: {
        ...earningRule,
        ...limitValueFromEarningRule,
        displayBlackoutPeriod: earningRule?.activePeriodValues?.map((item) =>
          getReadablePeriod(item.startDate, item.endDate, false),
        ),
        displayBlackoutWeekend: earningRule?.activeWeekday?.map(
          (item) => WEEKDAY[item],
        ),
      },
      fields: [
        [{ title: 'For stamp campaign', field: 'displayLinkedStampCampaign' }],
        ...targetCustomerDisplayFields,
        [{ title: 'Active period', field: 'displayActivePeriod' }],
        [
          {
            title: 'Blackout period (by period)',
            field: 'displayBlackoutPeriod',
          },
        ],
        [
          {
            title: 'Blackout period (by weekday)',
            field: 'displayBlackoutWeekend',
          },
        ],
        ...limitFields,
      ],
    }],
    [EarningRuleType.levelUpgrade]: [{
      title: 'Level Upgrade',
      data: {
        ...earningRule,
        ...limitValueFromEarningRule,
        levelGoal: renderLevelGoal(earningRule?.levelGoal),
        displayBlackoutPeriod: earningRule?.activePeriodValues?.map((item) =>
          getReadablePeriod(item.startDate, item.endDate, false),
        ),
        displayBlackoutWeekend: earningRule?.activeWeekday?.map(
          (item) => WEEKDAY[item],
        ),
      },
      fields: [
        [{ title: 'For stamp campaign', field: 'displayLinkedStampCampaign' }],
        [{ title: 'Level upgrade (from, to)', field: 'levelGoal' }],
        ...targetCustomerDisplayFields,
        [{ title: 'Active period', field: 'displayActivePeriod' }],
        [
          {
            title: 'Blackout period (by period)',
            field: 'displayBlackoutPeriod',
          },
        ],
        [
          {
            title: 'Blackout period (by weekday)',
            field: 'displayBlackoutWeekend',
          },
        ],
        ...levelUpgradelimitFields,
      ],
    }],
  };

  const getDisplayRewardAmount = () => {
    let displayRewardAmount = ""
    if (
      earningRule?.rewardAmountType === REWARD_AMOUNT_TYPE.oneOff
    ) {
      displayRewardAmount += "One-off fixed amount"
    } else if (
      earningRule?.rewardAmountType === REWARD_AMOUNT_TYPE.dynamic
    ) {
      displayRewardAmount += "Dynamic"
    }
    if (
      earningRule?.rewardAmountType === REWARD_AMOUNT_TYPE.dynamic
      && earningRule?.lineItemRequirement
      && earningRule?.rewardAmountBasedType
    ) {
      displayRewardAmount += `\n${DISPLAY_REWARD_AMOUNT_BASED_TYPE?.[earningRule?.rewardAmountBasedType]}`
    }
    if (earningRule?.rewardAmountType === REWARD_AMOUNT_TYPE.oneOff) {
      if (earningRule.rewardType === EarningRuleRewardType.stamps) {
        displayRewardAmount += `\n${earningRule?.stampTypeRewardPerXValue || '-'} stamps`
        return displayRewardAmount
      }
      if (earningRule.rewardType === EarningRuleRewardType.coupons) {
        displayRewardAmount += `\n${earningRule?.couponTypeRewardPerXValue || '-'} coupons`
        return displayRewardAmount
      }
      if (earningRule.rewardType === EarningRuleRewardType.points) {
        displayRewardAmount += `\n${earningRule?.pointTypeRewardPerXValue || '-'} pts; ${earningRule?.tpeTypeRewardPerXValue || '-'} pp`
        return displayRewardAmount
      }
    }
    if (earningRule?.rewardAmountType === REWARD_AMOUNT_TYPE.dynamic) {
      const per = earningRule?.rewardLogicType === REWARD_LOGIC_TYPE.baseOnItemQuantity ? "Every" : "HK$";
      const unit = earningRule?.rewardLogicType === REWARD_LOGIC_TYPE.baseOnItemQuantity ? "number of item =" : "="

      if (earningRule.rewardType === EarningRuleRewardType.stamps) {
        displayRewardAmount += `\n${per} ${earningRule?.stampTypeRewardPerX || '-'} ${unit} ${earningRule?.stampTypeRewardPerXValue || '-'} stamps`
        return displayRewardAmount
      }
      if (earningRule.rewardType === EarningRuleRewardType.coupons) {
        displayRewardAmount += `\n${per} ${earningRule?.couponTypeRewardPerX || '-'} ${unit} ${earningRule?.couponTypeRewardPerXValue || '-'} coupons`
        return displayRewardAmount
      }
      if (earningRule.rewardType === EarningRuleRewardType.points) {
        displayRewardAmount += `\n${per} ${earningRule?.pointTypeRewardPerX || '-'} ${unit} ${earningRule?.pointTypeRewardPerXValue || '-'} pts`
        displayRewardAmount += `\n${per} ${earningRule?.tpeTypeRewardPerX || '-'} ${unit} ${earningRule?.tpeTypeRewardPerXValue || '-'} pp`
        return displayRewardAmount
      }
    }
    return displayRewardAmount

  };

  const rewardSectionDataSource = {
    DEFAULT: {
      title: 'Reward',
      data: {
        ...earningRule,
        rewardType: displayRewardType,
      },
      fields: rewardFields,
    },
    [EarningRuleType.generalPurchase]: {
      title: 'Reward',
      data: {
        ...earningRule,
        displayRewardLogicType: DISPLAY_REWARD_LOGIC_TYPE?.[earningRule?.rewardLogicType] || "",
        displayRewardAmount: getDisplayRewardAmount(),
        rewardType: displayRewardType,
      },
      fields: rewardFields,
    }
  }
  useEffect(() => {
    dispatch(createAction('language/getList')({ isSelectorLoad: true }));
    dispatch(createAction('createEarningRules/getOneEarningRule')({ id }));

    return () => dispatch({ type: 'createEarningRules/clearData' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      id &&
      earningRule?.type === EarningRuleType.fillingForm
    ) {
      dispatch(
        createAction('createEarningRules/getWebhookAuthenticatedKey')({
          earningRuleId: id,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earningRule]);

  const getValueFromTranslations = (language, valueKeys) => {
    const data = {};
    if (valueKeys?.length > 0) {
      valueKeys.forEach((valueKey) => {
        data[valueKey] = earningRule?.translations?.[language]?.[valueKey];
      });
    }
    return data;
  };

  const renderTabForLanguage = (language) => {
    const data = getValueFromTranslations(language, [
      'name',
      'instructionSectionTitle',
      'instructionSectionContent',
      'detailSectionTitle',
      'detailSectionContent',
    ]);
    if (!data?.name) {
      data.name = earningRule.generalName;
    }

    const extraFields =
      earningRule?.linkedStampCampaign ||
        earningRule?.type === EarningRuleType.newMember ||
        earningRule?.type === EarningRuleType.birthday ||
        earningRule?.type === EarningRuleType.generalPurchase ||
        earningRule?.type === EarningRuleType.fillingForm ||
        earningRule?.type === EarningRuleType.levelUpgrade
        ? []
        : [
          [
            {
              title: i18n.t('instruction_section_title', {
                locale: language,
              }),
              field: 'instructionSectionTitle',
            },
          ],
          [
            {
              title: i18n.t('instruction_section_content', {
                locale: language,
              }),
              field: 'instructionSectionContent',
            },
          ],
          [
            {
              title: i18n.t('detail_section_title', { locale: language }),
              field: 'detailSectionTitle',
            },
          ],
          [
            {
              title: i18n.t('detail_section_content', { locale: language }),
              field: 'detailSectionContent',
            },
          ],
        ];

    return (
      <MembershipInfoCard
        title={i18n.t('content', { locale: language })}
        data={data}
        fields={[
          [{ title: i18n.t('name', { locale: language }), field: 'name' }],
          ...extraFields,
        ]}
      />
    );
  };
  const getSections = (sectionsConfig = []) => {
    let sections = []
    if (sectionsConfig?.length > 0) {
      sections = sectionsConfig?.map(({ title, data, fields }) => {
        if (title && data && fields) {
          return <MembershipInfoCard title={title} data={data} fields={fields} />
        }
        return <></>;
      })
    }

    return sections
  }


  const section = ({ title, data, fields }) => {
    if (title && data && fields) {
      return [<MembershipInfoCard title={title} data={data} fields={fields} />];
    }
    return [];
  };
  const getRewardLimitFields = () => {
    const getDisplayTimePeriod = (startTime, endTime) => (
      startTime && endTime
        ? `${getCampaignPeriodDateTime(startTime)} - ${getCampaignPeriodDateTime(endTime)}`
        : ""
    )
    return {
      displayEarningRuleAllTimeMaxAmount:
        `${earningRule?.earningRuleAllTimeLimitCoupons || '-'} coupons\n` +
        `${earningRule?.earningRuleAllTimeLimitPoints || '-'} points\n` +
        `${earningRule?.earningRuleAllTimeLimitPp || '-'} progression points\n` +
        `${earningRule?.earningRuleAllTimeLimitStamps || '-'} stamps`,
      displayEarningRuleTimePeriodPerXDay:
        earningRule?.earningRuleTimePeriodLimitPeriodicType === EARNING_RULE_PERIODIC_TYPE.perDays
          ? "Yes" : "No",
      displayEarningRuleTimePeriodSpecialPeriod:
        earningRule?.earningRuleTimePeriodLimitPeriodicType === EARNING_RULE_PERIODIC_TYPE.specificTime
          ? "Yes" : "No",
      displayEarningRuleTimePeriodFollowActivePeriod: earningRule?.earningRuleTimePeriodLimitPeriodicFollowActive
        ? "Yes" : "No",
      displayEarningRuleCustomisedTimePeriod:
        earningRule?.earningRuleTimePeriodLimitPeriodicFollowActive
          ? "" : getDisplayTimePeriod(
            earningRule?.earningRuleTimePeriodLimitPeriodicStartTime,
            earningRule?.earningRuleTimePeriodLimitPeriodicEndTime,
          ),
      displayEarningRuleSpecialTimeMaxAmount:
        `${earningRule?.earningRuleTimePeriodLimitCoupons || '-'} coupons\n` +
        `${earningRule?.earningRuleTimePeriodLimitPoints || '-'} points\n` +
        `${earningRule?.earningRuleTimePeriodLimitPp || '-'} progression points\n` +
        `${earningRule?.earningRuleTimePeriodLimitStamps || '-'} stamps`,

      displayPerMemberAllTimeMaxAmount:
        `${earningRule?.perMemberAllTimeLimitCoupons || '-'} coupons\n` +
        `${earningRule?.perMemberAllTimeLimitPoints || '-'} points\n` +
        `${earningRule?.perMemberAllTimeLimitPp || '-'} progression points\n` +
        `${earningRule?.perMemberAllTimeLimitStamps || '-'} stamps`,
      displayPerMemberTimePeriodPerXDay:
        earningRule?.perMemberTimePeriodLimitPeriodicType === EARNING_RULE_PERIODIC_TYPE.perDays
          ? "Yes" : "No",
      displayPerMemberTimePeriodSpecialPeriod:
        earningRule?.perMemberTimePeriodLimitPeriodicType === EARNING_RULE_PERIODIC_TYPE.specificTime
          ? "Yes" : "No",
      displayPerMemberTimePeriodFollowActivePeriod: earningRule?.perMemberTimePeriodLimitPeriodicFollowActivePeriod
        ? "Yes" : "No",
      displayPerMemberCustomisedTimePeriod:
        earningRule?.perMemberTimePeriodLimitPeriodicFollowActivePeriod
          ? "" : getDisplayTimePeriod(
            earningRule?.perMemberTimePeriodLimitPeriodicStartTime,
            earningRule?.perMemberTimePeriodLimitPeriodicEndTime,
          ),
      displayPerMemberSpecialTimeMaxAmount:
        `${earningRule?.perMemberTimePeriodLimitCoupons || '-'} coupons\n` +
        `${earningRule?.perMemberTimePeriodLimitPoints || '-'} points\n` +
        `${earningRule?.perMemberTimePeriodLimitPp || '-'} progression points\n` +
        `${earningRule?.perMemberTimePeriodLimitStamps || '-'} stamps`,
      displayPerTransactionMaxAmount:
        `${earningRule?.perTransactionLimitCoupons || '-'} coupons\n` +
        `${earningRule?.perTransactionLimitPoints || '-'} points\n` +
        `${earningRule?.perTransactionLimitPp || '-'} progression points\n` +
        `${earningRule?.perTransactionLimitStamps || '-'} stamps`,
    }
  }

  const tabs = [
    {
      name: 'Detail',
      content:
        createStatus === APIStatus.calling || !skusHaveLoaded ? (
          <Loading />
        ) : (
          <>
            <ContentSections
              hidePreview
              activeSection={LanguageConfig.english}
              contentStyle="campaign-detail-language-content-sections"
              languageTabContent={{
                containers: languages?.map((item) => ({
                  container: [renderTabForLanguage(item.code)],
                  key: item.code,
                  title: item.sourceName,
                })),
              }}
            />
            <ContentSections
              sections={getSections(dataSource?.[earningRule?.type] || {})}
              hidePreview
              contentStyle="campaign-detail-language-content-sections"
            />
            <ContentSections
              sections={section(rewardSectionDataSource?.[earningRule?.type] || rewardSectionDataSource.DEFAULT)}
              hidePreview
              contentStyle="campaign-detail-language-content-sections"
            />
            <ContentSections
              sections={section({
                title: 'REWARD LIMIT(OPTIONAL)',
                data: {
                  ...earningRule,
                  ...getRewardLimitFields(),
                },
                fields: [
                  [{ title: 'Maximum amount of rewards this earning rule can be issued in all time', field: 'displayEarningRuleAllTimeMaxAmount' }],
                  [{ title: 'Maximum number of times this earning rule can be triggered in all time', field: 'earningRuleAllTimeLimitMaxTriggerTimes' }],
                  [{ title: `[Earning rule]Time period - per ${earningRule?.earningRuleTimePeriodLimitPeriodicTypePerDays || "X"} days`, field: 'displayEarningRuleTimePeriodPerXDay' }],
                  [{ title: '[Earning rule]Time period - Specific start and end time', field: 'displayEarningRuleTimePeriodSpecialPeriod' }],
                  [{ title: '[Earning rule]Time period follow active period', field: 'displayEarningRuleTimePeriodFollowActivePeriod' }],
                  [{ title: '[Earning rule]Customised time period', field: 'displayEarningRuleCustomisedTimePeriod' }],
                  [{ title: 'Maximum amount of rewards this earning rule can be issued within a specified time period', field: 'displayEarningRuleSpecialTimeMaxAmount' }],
                  [{ title: 'Maximum number of times this earning rule can be triggered within a specified time period', field: 'earningRuleTimePeriodLimitMaxTriggerTimes' }],
                  [{ title: 'Maximum amount of rewards this earning rule can be issued per member in all time', field: 'displayPerMemberAllTimeMaxAmount' }],
                  [{ title: 'Maximum number of times this earning rule can be triggered per member in all time', field: 'perMemberAllTimeLimitMaxTriggerTimes' }],
                  [{ title: `[Per member]Time period - per ${earningRule?.perMemberTimePeriodLimitPeriodicTypePerDays || "X"} days`, field: 'displayPerMemberTimePeriodPerXDay' }],
                  [{ title: '[Per member]Time period - Specific start and end time', field: 'displayPerMemberTimePeriodSpecialPeriod' }],
                  [{ title: '[Per member]Time period follow active period', field: 'displayPerMemberTimePeriodFollowActivePeriod' }],
                  [{ title: '[Per member]Customised time period', field: 'displayPerMemberCustomisedTimePeriod' }],
                  [{ title: 'Maximum amount of rewards this earning rule can be issued per member within a specified time period', field: 'displayPerMemberSpecialTimeMaxAmount' }],
                  [{ title: 'Maximum number of times this earning rule can be triggered per member within a specified time period', field: 'perMemberTimePeriodLimitMaxTriggerTimes' }],
                  [{ title: 'Maximum amount of rewards this earning rule can be issued per transaction', field: 'displayPerTransactionMaxAmount' }],
                ],
              })}
              hidePreview
              contentStyle="campaign-detail-language-content-sections"
            />
            <ContentSections
              sections={section({
                title: "GENERAL",
                data: earningRule,
                fields: [
                  [{ title: 'Creation time', field: 'displayCreationDate' }],
                  [{ title: 'Last modified time', field: 'displayLastModifiedDate' }],
                  [{ title: 'Last modified by', field: 'displayLastModifiedBy' }],
                ]
              })}
              hidePreview
            />
          </>
        ),
    },
  ];

  const exportNewMemberEarningRuleCustomers = () => {
    dispatch({
      type: 'downloadAndImport/createDownloadTask',
      payload: {
        from: ImportResource.customer,
        related: {
          referralEarningRule: earningRule.pk,
        }
      },
    });
  }

  const copiedToastAction = () => {
    dispatch({
      type: 'navBars/updateState',
      payload: {
        saveDiscardToastShowing: {
          value: true,
          type: ToastType.copied,
        },
      },
    });
  };

  const copyWebhookURLAction = () => {
    navigator.clipboard.writeText(
      `${process.env.REACT_APP_DOMAIN}/webhooks/earning_rules/${id}`,
    );
    copiedToastAction();
  };

  const copyWebhookAuthenticatedKeyAction = (authenticatedKey) => {
    navigator.clipboard.writeText(authenticatedKey?.key);
    dispatch({
      type: 'createEarningRules/createOrUpdateWebhookAuthenticatedKey',
      payload: { id: authenticatedKey?.pk, isCopied: true },
    });
    copiedToastAction();
  };

  const copyAuthenticationKeyAction = () => {
    setShowPrompt(true);
    setPromptConfig({
      title: 'Keep the authenticated key save',
      description: `Upon click "Confirm" below, the authenticated key will be copied to the clipboard. Please make sure you save it, you won't be able to access it again.`,
      leftButton: {
        text: 'Cancel',
        action: () => {
          setShowPrompt(false);
          setPromptConfig({});
        },
      },
      rightButton: {
        text: 'Confirm',
        action: () => {
          copyWebhookAuthenticatedKeyAction(webhookAuthenticatedKey);
          setShowPrompt(false);
          setPromptConfig({});
        },
      },
    });
  };

  const generateAuthenticationKeyAction = () => {
    setShowPrompt(true);
    setPromptConfig({
      title: 'Heads up',
      description: `Upon click "Confirm" below, a new authenticated key will be copied to your clipboard but the the existing authentication key would become invalid.`,
      leftButton: {
        text: 'Cancel',
        action: () => {
          setShowPrompt(false);
          setPromptConfig({});
        },
      },
      rightButton: {
        text: 'Confirm',
        action: () => {
          dispatch({
            type: 'createEarningRules/createOrUpdateWebhookAuthenticatedKey',
            payload: {
              earningRuleId: id,
              afterAction: (authenticatedKey) => {
                copyWebhookAuthenticatedKeyAction(authenticatedKey);
              },
            },
          });
          setShowPrompt(false);
          setPromptConfig({});
        },
      },
    });
  };

  const extraButtons = () => {
    const earningRuleType = earningRule?.type
    if (earningRuleType === EarningRuleType.fillingForm) {
      return {
        extraButtons: [
          <AuthButton
            title="Guide"
            customClass="btn-back-button-common btn-download"
            href={`${ImageDomain}Webhook-Guide.pdf`}
            target={'_blank'}
            requires={PermissionCodes.changeEarningRule}
          />,
          <AuthButton
            title="Copy Webhook URL"
            customClass="btn-back-button-common btn-download"
            action={copyWebhookURLAction}
            requires={PermissionCodes.changeEarningRule}
          />,
          <AuthButton
            title={'Copy Authenticated Key'}
            customClass="btn-back-button-common btn-download"
            action={
              isCopied
                ? generateAuthenticationKeyAction
                : copyAuthenticationKeyAction
            }
            requires={PermissionCodes.changeEarningRule}
          />,
        ],
        extraPopContent: [
          {
            requires: PermissionCodes.changeEarningRule,
            action: () => { },
            content: 'Guide',
          },
          {
            requires: PermissionCodes.changeEarningRule,
            action: copyWebhookURLAction,
            content: 'Copy Webhook URL',
          },
          {
            requires: PermissionCodes.changeEarningRule,
            action: isCopied
              ? generateAuthenticationKeyAction
              : copyAuthenticationKeyAction,
            content: 'Copy Authenticated Key',
          },
        ]
      }
    }
    if (earningRuleType === EarningRuleType.newMember) {
      return {
        extraButtons: [
          <CustomButtonWithTips
            tipsContent={"Export customers list who used this promotion code to sign up."}
            title="Export"
            action={exportNewMemberEarningRuleCustomers}
            customClass="btn-back-button-common btn-download"
            requires={PermissionCodes.viewEarningRule}
          />,
        ],
        extraPopContent: [
          {
            requires: PermissionCodes.viewEarningRule,
            action: exportNewMemberEarningRuleCustomers,
            content: 'Export',
          },
        ],
      }
    }

    return {}
  }

  const buttons = [
    <ListButtonsGroup
      primaryButton={
        <AuthButton
          title="Edit"
          action={() => {
            history.push({
              pathname: 'edit/',
            });
          }}
          requires={PermissionCodes.changeEarningRule}
        />
      }
      primaryPopContent={{
        requires: PermissionCodes.changeEarningRule,
        action: () => {
          history.push({
            pathname: 'edit/',
          });
        },
        content: 'Edit',
      }}
      {...extraButtons()}
    />,
  ];

  return (
    <>
      <CustomListComponent
        caution={{
          detail: '',
          title: earningRule.generalName,
        }}
        hideTab
        breadcrumb={<CustomBreadcrumb name={earningRule.generalName} />}
        buttons={buttons}
        tabs={tabs}
      />
      <BasePrompt
        show={showPrompt}
        closeAction={() => {
          setShowPrompt(false);
          setPromptConfig({});
        }}
        leftButton={{ ...promptConfig?.leftButton }}
        rightButton={{ ...promptConfig?.rightButton }}
        title={promptConfig?.title}
        description={promptConfig?.description}
      />
    </>
  );
}

const mapPropsToState = (state) => ({
  earningRule: state.createEarningRules.earningRule,
  createStatus: state.createEarningRules.createStatus,
  languages: state.language.allList,
  levelSource: state.levels.levelList,
  webhookAuthenticatedKey: state.createEarningRules.webhookAuthenticatedKey,
  isCopied: state.createEarningRules.isCopied,
  skusHaveLoaded: state.createEarningRules.skusHaveLoaded,
});

export default connect(mapPropsToState)(EarningRuleDetail);
